import { z } from 'zod';

/**
 * Slack identity of the person who triggered an agent run from Slack.
 * Stored for traceability — `superfunnelUserId` is set when the Slack email
 * matches a SuperFunnel user on the account.
 */
const SlackActorSchema = z.object({
    slackUserId: z.string(),
    slackTeamId: z.string(),
    email: z.string().nullish(),
    name: z.string().nullish(),
    superfunnelUserId: z.string().nullish(),
});
/**
 * Payload for the Cloud Task that processes an inbound Slack event
 * (app mention, DM, or threaded follow-up) and runs the super agent.
 *
 * Enqueued by apps/functions (slack/events receiver) and processed by
 * apps/functions-ai-agent (process-slack-event queue task).
 */
const SlackEventTaskPayloadSchema = z.object({
    /** Slack event ID (Ev...) — used for tracing/idempotency */
    eventId: z.string(),
    /** Slack event type: 'app_mention' or 'message' */
    eventType: z.string(),
    /** Slack workspace (team) ID */
    teamId: z.string(),
    /** Channel the message was posted in (C... / G... / D...) */
    channelId: z.string(),
    /** Slack channel type: 'channel' | 'group' | 'im' (absent on app_mention) */
    channelType: z.string().nullish(),
    /** Slack user who sent the message */
    slackUserId: z.string(),
    /** Message text (mention markup included for app_mention) */
    text: z.string(),
    /** Timestamp (message id) of the triggering message */
    messageTs: z.string(),
    /** Parent thread timestamp when the message is inside a Slack thread */
    threadTs: z.string().nullish(),
});
/**
 * Bot scopes requested when installing the SuperFunnel Slack app. Canonical
 * list shared by the dashboard (OAuth install URL) and the backend (OAuth
 * exchange) so the requested scopes can never drift between the two. Keep in
 * sync with the Slack app manifest.
 */
const SLACK_BOT_SCOPES = [
    'app_mentions:read',
    'chat:write',
    'im:history',
    'im:write',
    'channels:history',
    'channels:read',
    'groups:history',
    'groups:read',
    'reactions:write',
    'users:read',
    'users:read.email',
];
/**
 * Inbound Slack event payload (the inner `event` of an event_callback).
 * Slack sends many more fields than we use; `.passthrough()` keeps them while
 * still validating the shape of the fields the receiver relies on.
 */
const SlackInboundEventSchema = z
    .object({
    type: z.string(),
    subtype: z.string().nullish(),
    user: z.string().nullish(),
    bot_id: z.string().nullish(),
    text: z.string().nullish(),
    channel: z.string().nullish(),
    channel_type: z.string().nullish(),
    ts: z.string().nullish(),
    thread_ts: z.string().nullish(),
    tab: z.string().nullish(),
})
    .passthrough();
/**
 * Slack Events API request envelope (url_verification handshake or
 * event_callback). Validated after signature verification.
 */
const SlackEventEnvelopeSchema = z
    .object({
    type: z.string(),
    challenge: z.string().nullish(),
    event_id: z.string().nullish(),
    team_id: z.string().nullish(),
    event: SlackInboundEventSchema.nullish(),
})
    .passthrough();
/**
 * Slack Interactivity request body. Interaction payloads arrive form-encoded
 * as a single JSON-string `payload` field.
 */
const SlackInteractionBodySchema = z
    .object({
    payload: z.string().nullish(),
})
    .passthrough();
/**
 * Query parameters Slack appends to the OAuth callback redirect.
 */
const SlackOAuthCallbackQuerySchema = z
    .object({
    code: z.string().nullish(),
    state: z.string().nullish(),
    error: z.string().nullish(),
})
    .passthrough();

export { SLACK_BOT_SCOPES, SlackActorSchema, SlackEventEnvelopeSchema, SlackEventTaskPayloadSchema, SlackInboundEventSchema, SlackInteractionBodySchema, SlackOAuthCallbackQuerySchema };
//# sourceMappingURL=slack.esm.js.map
